const _jsxFileName = '';
function _nullishCoalesce(lhs, rhsFn) {
  if (lhs != null) {
    return lhs;
  } else {
    return rhsFn();
  }
}
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  ArrowLeft,
  XCircle,
  Upload,
  Eye,
  FileText,
  CreditCard,
  Truck,
  Save,
  User,
  Image as ImageIcon,
  AlertCircle,
  RefreshCw,
  Package,
  Info,
  MessageSquare,
  X,
  Loader,
  Store,
} from 'lucide-react';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
});

// Placeholder image constant
const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/300?text=No+Image';

// Auto-generated log details based on order status (NO EMOJIS)
const AUTO_LOG_DETAILS = {
  Pending: [
    'Order received and awaiting payment verification',
    'Payment confirmation pending - customer will upload proof of payment',
    'Order on hold until payment is verified',
  ],
  Paid: [
    'Payment verified by finance team - order confirmed',
    'Order has been paid and is now being processed',
    'Payment confirmation received - preparing for fulfillment',
  ],
  Preparing: [
    'Order assigned to fulfillment team for preparation',
    'Items are being carefully packaged and inspected',
    'Quality control check in progress',
    'Security seal applied to package',
  ],
  Shipped: [
    'Order handed over to logistics partner for delivery',
    'Package in transit to sorting facility',
    'Order Out for delivery',
    'Shipment dispatched from Sparkling Gems warehouse',
    'Tracking number assigned and updated',
  ],
  'Ready for Pickup': [
    'Order ready for pickup at Sparkling Gems Jewelry Shop',
    'Items are ready - customer notified for pickup',
    'Order available for collection at store location',
  ],
  'Picked Up': [
    'Order picked up by customer - ID verified',
    'Items collected from shop successfully',
    'Pickup confirmation received from customer',
  ],
  Delivered: [
    'Order delivered successfully to recipient',
    'Delivery confirmation received from courier',
    'Package received by customer - signature on file',
    'Customer satisfaction follow-up scheduled',
  ],
  Rejected: [
    'Payment verification failed - order rejected',
    'Invalid payment proof provided - action required',
    'Rejection notice sent to customer for resubmission',
    'Payment could not be verified - order on hold',
  ],
};

/**
 * Extended order type to handle API response
 */

const AdminTransactions = ({ orders = [], onUpdateOrder, onNavigate, user }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [viewMode, setViewMode] = useState('payment');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  const [orderTimeline, setOrderTimeline] = useState([]);
  const [paymentProofData, setPaymentProofData] = useState(null);
  const [proofLoading, setProofLoading] = useState(false);

  // Rejection Modal State
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  // PENDING CHANGES
  const [pendingStatus, setPendingStatus] = useState(null);
  const [pendingTrackingNumber, setPendingTrackingNumber] = useState('');
  const [pendingLogNote, setPendingLogNote] = useState('');
  const [pendingLogLocation, setPendingLogLocation] = useState('');
  const [pendingLogProof, setPendingLogProof] = useState(null);
  const [pendingLogProofFile, setPendingLogProofFile] = useState(null);

  // UI State for Feedback
  const [isSavingFulfillment, setIsSavingFulfillment] = useState(false);
  const [fulfillmentSaveSuccess, setFulfillmentSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Local state for real-time updates
  const [localOrders, setLocalOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Helper function to extract numeric ID from order ID
  const extractNumericId = (orderId) => {
    if (typeof orderId === 'number') return orderId;
    if (typeof orderId === 'string') {
      const numericPart = orderId.replace(/[^0-9]/g, '');
      const result = parseInt(numericPart, 10);
      return isNaN(result) ? 0 : result;
    }
    return 0;
  };

  // Helper function to get image URL from timeline event
  const getTimelineImageUrl = (event) => {
    if (event.image_url) {
      return event.image_url;
    }
    if (event.id && event.has_image) {
      return `${API_BASE_URL}/get-image?id=${event.id}`;
    }
    if (event.proof_image && !event.proof_image.startsWith('blob:')) {
      if (event.proof_image.startsWith('http')) {
        return event.proof_image;
      }
      return `${API_BASE_URL}${event.proof_image}`;
    }
    return PLACEHOLDER_IMAGE;
  };

  // Helper function to get auto-generated log description based on status (NO EMOJIS)
  const getAutoLogDescription = (status) => {
    const statusKey =
      status === 'Preparing' ? 'Preparing' : status === 'Processing' ? 'Preparing' : status;

    const descriptions = AUTO_LOG_DETAILS[statusKey] || AUTO_LOG_DETAILS['Pending'];
    const randomIndex = Math.floor(Math.random() * descriptions.length);

    return descriptions[randomIndex];
  };

  // Update URL when component mounts
  useEffect(() => {
    window.history.pushState(null, '', '/admin/transactions');
  }, []);

  // Fetch orders on component mount
  useEffect(() => {
    fetchOrdersFromDatabase();
  }, []);

  // Safe string conversion
  const safeString = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (e2) {
        return '';
      }
    }
    return '';
  };

  const safeLower = (v) => {
    return safeString(v).toLowerCase();
  };

  const isPickupOrder = (order) => {
    if (!order) return false;
    return (
      order.deliveryType === 'pickup' ||
      order.delivery_type === 'pickup' ||
      !!(order.shippingAddress && order.shippingAddress.includes('Store Pickup'))
    );
  };

  // Check if there are any pending changes
  const hasPendingChanges = () => {
    if (!selectedOrder) return false;
    const statusChanged = pendingStatus !== null && pendingStatus !== selectedOrder.status;
    const trackingChanged = pendingTrackingNumber !== (selectedOrder.trackingNumber || '');
    const logEntryAdded =
      pendingLogNote.trim() !== '' || pendingLogProof !== null || pendingLogLocation.trim() !== '';
    return statusChanged || trackingChanged || logEntryAdded;
  };

  // FIXED: Enhanced fetch payment proof with better error handling
  const fetchPaymentProof = useCallback(async (orderId) => {
    if (!orderId) return;

    setProofLoading(true);
    setPaymentProofData(null);

    try {
      const numericId = extractNumericId(orderId);
      if (!numericId) {
        setProofLoading(false);
        return;
      }

      // Add timestamp to prevent caching
      const timestamp = Date.now();
      const url = `${API_BASE_URL}/orders/get-payment-proof?order_id=${numericId}&_=${timestamp}`;

      const response = await fetch(url, {
        headers: {
          ...authHeaders(),
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.has_proof && data.proof) {
        setPaymentProofData(data.proof);

        // Update the selected order with the proof data
        setSelectedOrder((prev) => {
          if (prev && prev.id === orderId) {
            return {
              ...prev,
              paymentProof: data.proof.file_path,
              payment_proof: data.proof.file_path,
              transactionId: data.proof.transaction_id || prev.transactionId,
              transaction_id: data.proof.transaction_id,
              file_path: data.proof.file_path,
              proof_file_name: data.proof.file_name,
              proof_uploaded_at: data.proof.uploaded_at,
              has_proof: true,
              proof_source: data.proof_source,
              proof_status: data.proof.status,
            };
          }
          return prev;
        });
      } else {
        setPaymentProofData(null);
      }
    } catch (error) {
      setPaymentProofData(null);
    } finally {
      setProofLoading(false);
    }
  }, []);

  // Fetch timeline
  const fetchTimeline = useCallback(async (orderId) => {
    try {
      const numericId = extractNumericId(orderId);
      if (!numericId) {
        return;
      }
      const timestamp = new Date().getTime();
      const response = await fetch(
        `${API_BASE_URL}/orders/get-timeline?order_id=${numericId}&_=${timestamp}`,
        { headers: authHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        const sortedTimeline = (data.timeline || []).sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setOrderTimeline(sortedTimeline);
      } else {
        setOrderTimeline([]);
      }
    } catch (error) {
      setOrderTimeline([]);
    }
  }, []);

  // Fetch payment proof and timeline when selected order changes
  useEffect(() => {
    if (!_optionalChain([selectedOrder, 'optionalAccess', (_) => _.id])) return;

    // Fetch timeline
    fetchTimeline(selectedOrder.id);

    // Fetch payment proof when viewing payment tab
    if (viewMode === 'payment') {
      fetchPaymentProof(selectedOrder.id);
    }
  }, [
    _optionalChain([selectedOrder, 'optionalAccess', (_2) => _2.id]),
    viewMode,
    fetchPaymentProof,
    fetchTimeline,
  ]);

  // Fetch fresh orders from API
  const fetchOrdersFromDatabase = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const timestamp = Date.now();
      const response = await fetch(`${API_BASE_URL}/orders/get?admin=true&_=${timestamp}`, {
        headers: authHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        let ordersData = [];

        // Handle different response formats
        if (Array.isArray(data.orders)) {
          ordersData = data.orders;
        } else if (Array.isArray(data.data)) {
          ordersData = data.data;
        } else if (Array.isArray(data)) {
          ordersData = data;
        }

        // Process orders — map snake_case API fields to camelCase expected by component
        const processedOrders = ordersData.map((order) => ({
          ...order,
          total: parseFloat(order.total_amount) || 0,
          totalAmount: parseFloat(order.total_amount) || 0,
          order_date: order.order_date || order.created_at,
          date: order.order_date || order.created_at,
          customerName: order.customer_name || order.customerName || 'Guest',
          customerEmail: order.customer_email || order.customerEmail || '',
          customerPhone: order.customer_phone || order.customerPhone || '',
          shippingAddress: order.shipping_address || order.shippingAddress || '',
          paymentMethod: order.payment_method || order.paymentMethod || '',
          paymentProof: order.payment_proof || order.paymentProof || null,
          transactionId: order.transaction_id || order.transactionId || '',
          deliveryType: order.delivery_type || order.deliveryType || 'shipping',
          pickupName: order.pickup_name || order.pickupName || '',
          pickupPhone: order.pickup_phone || order.pickupPhone || '',
          trackingNumber: order.tracking_number || order.trackingNumber || '',
          items:
            typeof order.items === 'string' ? JSON.parse(order.items || '[]') : order.items || [],
          has_proof: !!(order.paymentProof || order.payment_proof || order.file_path),
          proof_status: order.proof_status || 'pending',
        }));

        setLocalOrders(processedOrders);

        // Update parent if callback exists
        if (onUpdateOrder && ordersData.length > 0) {
          ordersData.forEach((order) => {
            onUpdateOrder(order.id, order);
          });
        }
      } else {
        setApiError(data.error || 'Failed to fetch orders');
        setLocalOrders([]);
      }
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'Connection error');
      setLocalOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Update order in database
  const updateOrderInDatabase = async (orderId, updates) => {
    try {
      const numericId = extractNumericId(orderId);
      if (!numericId) {
        throw new Error('Invalid order ID');
      }
      const response = await fetch(`${API_BASE_URL}/orders/update-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify({ order_id: numericId, ...updates }),
      });
      const data = await response.json();
      if (data.success) {
        setLocalOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, ...updates } : o)));
        if (onUpdateOrder) {
          onUpdateOrder(orderId, updates);
        }
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // Handle fulfillment save with auto-generated log details (NO EMOJIS)
  const handleSaveFulfillment = async () => {
    if (!selectedOrder) return;

    const statusChanged = pendingStatus !== null && pendingStatus !== selectedOrder.status;
    const trackingChanged = pendingTrackingNumber !== (selectedOrder.trackingNumber || '');
    const logEntryAdded =
      pendingLogNote.trim() !== '' || pendingLogProof !== null || pendingLogLocation.trim() !== '';

    if (pendingStatus === 'Shipped' && !pendingTrackingNumber.trim()) {
      showToast('Please enter a tracking number before marking order as Shipped.', 'error');
      return;
    }
    if (!statusChanged && !trackingChanged && !logEntryAdded) {
      showToast('No changes to save.', 'error');
      return;
    }

    setIsSavingFulfillment(true);
    setError(null);

    try {
      const numericId = extractNumericId(selectedOrder.id);
      if (!numericId) {
        throw new Error('Invalid order ID');
      }

      if (statusChanged || trackingChanged) {
        const updateData = { order_id: numericId };
        if (statusChanged) updateData.status = pendingStatus;
        if (trackingChanged) updateData.tracking_number = pendingTrackingNumber;

        const orderResponse = await fetch(`${API_BASE_URL}/orders/update-status`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
          },
          body: JSON.stringify(updateData),
        });

        const orderResult = await orderResponse.json();

        if (!orderResult.success) {
          throw new Error(orderResult.error || 'Failed to update order');
        }
      }

      if (logEntryAdded || statusChanged) {
        let description = '';
        if (statusChanged) {
          description = getAutoLogDescription(pendingStatus);
        } else if (trackingChanged) {
          description = `Tracking number assigned: ${pendingTrackingNumber}.`;
        } else if (pendingLogNote.trim() !== '') {
          description = pendingLogNote.trim();
        } else {
          description = 'Fulfillment update processed by admin.';
        }
        const statusToUse = statusChanged ? pendingStatus : selectedOrder.status;
        const timelineResponse = await fetch(`${API_BASE_URL}/orders/add-timeline`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            order_id: numericId,
            status: statusToUse || 'Pending',
            description,
            location: pendingLogLocation || 'Sparkling Gems HQ',
            proof_image: pendingLogProof || null,
            created_by: 'admin',
          }),
        });
        const timelineResult = await timelineResponse.json();
        if (!timelineResult.success) throw new Error('Failed to save timeline entry');
      }

      setSelectedOrder((prev) =>
        prev
          ? {
              ...prev,
              status: statusChanged && pendingStatus ? pendingStatus : prev.status,
              trackingNumber: trackingChanged ? pendingTrackingNumber : prev.trackingNumber,
            }
          : null
      );

      setPendingLogNote('');
      setPendingLogLocation('');
      setPendingLogProof(null);
      setPendingLogProofFile(null);
      setPendingStatus(statusChanged && pendingStatus ? pendingStatus : selectedOrder.status);

      setFulfillmentSaveSuccess(true);
      setTimeout(() => setFulfillmentSaveSuccess(false), 3000);

      await fetchOrdersFromDatabase();

      if (selectedOrder) {
        await fetchTimeline(selectedOrder.id);
      }

      showToast('Fulfillment updated successfully!');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to save fulfillment');
      showToast('Error saving fulfillment. Please try again.', 'error');
    } finally {
      setIsSavingFulfillment(false);
    }
  };

  // Get customer name
  const getCustomerName = (order) => {
    if (isPickupOrder(order)) {
      return order.pickup_name || order.customerName || order.name || order.full_name || 'Guest';
    }
    return _nullishCoalesce(
      _nullishCoalesce(
        _nullishCoalesce(order.customerName, () => order.name),
        () => order.full_name
      ),
      () => 'Guest'
    );
  };

  // Get delivery type display
  const getDeliveryTypeDisplay = (order) => {
    const isPickup = isPickupOrder(order);
    if (isPickup) {
      return {
        icon: React.createElement(Store, {
          size: 14,
          className: 'text-amber-600',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 568 },
        }),
        text: 'Store Pickup',
        color: 'bg-amber-50 text-amber-700 border-amber-200',
      };
    }
    return {
      icon: React.createElement(Truck, {
        size: 14,
        className: 'text-blue-600',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 574 },
      }),
      text: 'Shipping',
      color: 'bg-blue-50 text-blue-700 border-blue-200',
    };
  };

  // FIXED: Enhanced proof detection - checks all possible sources
  const hasProof = (order) => {
    return !!(
      order.paymentProof ||
      order.payment_proof ||
      order.file_path ||
      order.payment_proof ||
      order.has_proof === true ||
      order.proof_uploaded_at ||
      _optionalChain([paymentProofData, 'optionalAccess', (_3) => _3.file_path])
    );
  };

  // Get payment method display
  const getPaymentMethodDisplay = (order) => {
    const proofExists = hasProof(order);

    if (proofExists) {
      return {
        icon: React.createElement(
          'span',
          {
            className: 'text-gold-600 font-bold text-sm',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 599 },
          },
          '₱'
        ),
        text: 'Bank Transfer',
      };
    }

    const paymentMethod = (order.paymentMethod || '').toLowerCase();
    const isBankTransfer =
      paymentMethod.includes('bank') ||
      paymentMethod.includes('transfer') ||
      paymentMethod === 'bank_transfer';

    if (isBankTransfer) {
      return {
        icon: React.createElement(
          'span',
          {
            className: 'text-gold-600 font-bold text-sm',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 612 },
          },
          '₱'
        ),
        text: 'Bank Transfer',
      };
    }
    return {
      icon: React.createElement(CreditCard, {
        size: 14,
        className: 'text-stone-400',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 617 },
      }),
      text: 'Credit Card',
    };
  };

  // Filter orders
  const filteredOrders = localOrders.filter((order) => {
    try {
      const customerName = getCustomerName(order);
      const search = safeLower(searchTerm);
      const orderId = safeLower(
        _optionalChain([order, 'optionalAccess', (_4) => _4.order_number]) ||
          _optionalChain([order, 'optionalAccess', (_5) => _5.id])
      );
      const custName = safeLower(customerName);
      const transId = _optionalChain([order, 'optionalAccess', (_6) => _6.transactionId])
        ? safeLower(order.transactionId)
        : '';
      const matchesSearch =
        orderId.includes(search) || custName.includes(search) || transId.includes(search);
      const matchesFilter =
        filterStatus === 'All' ||
        safeLower(_optionalChain([order, 'optionalAccess', (_7) => _7.status])) ===
          safeLower(filterStatus);
      return matchesSearch && matchesFilter;
    } catch (e3) {
      return false;
    }
  });

  // Count pending proofs
  const pendingProofsCount = localOrders.filter(
    (o) => o.status === 'Pending' && !hasProof(o)
  ).length;

  // Lightbox controls
  const openLightbox = (imageUrl, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage('');
  };

  // Open manage modal
  const openManageModal = (order) => {
    setSelectedOrder(order);
    setViewMode('info');
    setPendingStatus(order.status);
    setPendingTrackingNumber(order.trackingNumber || '');
    setPendingLogNote('');
    setPendingLogLocation('');
    setPendingLogProof(null);
    setPendingLogProofFile(null);
    setRejectionReason('');
    setError(null);
    setPaymentProofData(null);
    setOrderTimeline([]);
  };

  // Handle payment approval with auto-generated description (NO EMOJIS)
  const handleApprovePayment = async () => {
    if (!selectedOrder) return;
    setIsSavingFulfillment(true);
    setError(null);
    try {
      const numericId = extractNumericId(selectedOrder.id);
      if (!numericId) {
        throw new Error('Invalid order ID');
      }

      const response = await fetch(`${API_BASE_URL}/orders/update-status`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ order_id: numericId, status: 'Paid' }),
      });
      const data = await response.json();

      if (data.success) {
        // Add timeline entry
        await fetch(`${API_BASE_URL}/orders/add-timeline`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            order_id: numericId,
            status: 'Paid',
            description: getAutoLogDescription('Paid'),
            created_by: 'admin',
          }),
        }).catch(() => {});

        setSelectedOrder((prev) => (prev ? { ...prev, status: 'Paid' } : null));
        await fetchOrdersFromDatabase();
        if (selectedOrder) {
          await fetchTimeline(selectedOrder.id);
        }
        setSelectedOrder(null);
        showToast('Payment confirmed successfully!');
      } else {
        throw new Error(data.error || 'Failed to confirm payment');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to confirm payment');
      showToast('Error approving payment. Please try again.', 'error');
    } finally {
      setIsSavingFulfillment(false);
    }
  };

  // Handle payment rejection with auto-generated description (NO EMOJIS)
  const handleRejectWithReason = async () => {
    if (!selectedOrder || !rejectionReason.trim()) return;
    setIsRejecting(true);
    try {
      const numericId = extractNumericId(selectedOrder.id);
      if (!numericId) {
        throw new Error('Invalid order ID');
      }
      const updates = {
        status: 'Rejected',
        rejection_reason: rejectionReason,
      };
      const response = await fetch(`${API_BASE_URL}/orders/update-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify({ order_id: numericId, ...updates }),
      });
      const data = await response.json();

      if (data.success) {
        await fetch(`${API_BASE_URL}/orders/add-timeline`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            order_id: numericId,
            status: 'Rejected',
            description: `${getAutoLogDescription('Rejected')} Reason: ${rejectionReason}`,
            created_by: 'admin',
          }),
        });
        setShowRejectModal(false);
        setSelectedOrder(null);
        await fetchOrdersFromDatabase();
        showToast('Payment rejected successfully.');
      } else {
        throw new Error(data.error || 'Failed to reject payment');
      }
    } catch (error) {
      showToast('Error rejecting payment. Please try again.', 'error');
    } finally {
      setIsRejecting(false);
    }
  };

  // Handle file upload
  const handleTrackingFileUpload = (e) => {
    const file = _optionalChain([
      e,
      'access',
      (_8) => _8.target,
      'access',
      (_9) => _9.files,
      'optionalAccess',
      (_10) => _10[0],
    ]);
    if (file) {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        showToast(`File size too large. Maximum ${maxSize / (1024 * 1024)}MB allowed.`, 'error');
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        showToast('Invalid file type. Please upload JPG, PNG, GIF, or WEBP.', 'error');
        return;
      }
      const url = URL.createObjectURL(file);
      setPendingLogProof(url);
      setPendingLogProofFile(file);
      e.target.value = '';
    }
  };

  // Tab change handler
  const handleTabChange = (tab) => {
    setViewMode(tab);
    if (tab === 'payment' && selectedOrder) {
      fetchPaymentProof(selectedOrder.id);
    }
  };

  // Input handlers
  const handleInputClick = (e) => e.stopPropagation();
  const handleStatusChange = (e) => {
    setPendingStatus(e.target.value);
    // Auto-generate log note based on selected status (NO EMOJIS)
    if (e.target.value) {
      setPendingLogNote(getAutoLogDescription(e.target.value));
    }
  };
  const handleTrackingChange = (e) => {
    setPendingTrackingNumber(e.target.value);
  };
  const handleLogNoteChange = (e) => {
    setPendingLogNote(e.target.value);
  };
  const handleLogLocationChange = (e) => {
    setPendingLogLocation(e.target.value);
  };
  const handleModalContentClick = (e) => e.stopPropagation();

  // FIXED: Get the payment proof image URL with better handling
  const getPaymentProofImage = () => {
    if (_optionalChain([selectedOrder, 'optionalAccess', (_11) => _11.paymentProof])) {
      return selectedOrder.paymentProof;
    }
    if (_optionalChain([selectedOrder, 'optionalAccess', (_12) => _12.payment_proof])) {
      return selectedOrder.payment_proof;
    }
    if (_optionalChain([selectedOrder, 'optionalAccess', (_13) => _13.file_path])) {
      return selectedOrder.file_path;
    }
    if (_optionalChain([paymentProofData, 'optionalAccess', (_14) => _14.file_path])) {
      return paymentProofData.file_path;
    }
    return null;
  };

  // Loading state
  if (loading) {
    return React.createElement(
      'div',
      {
        className: 'pt-44 pb-12 min-h-screen bg-stone-50',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 841 },
      },
      React.createElement(
        'div',
        {
          className: 'max-w-7xl mx-auto px-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 842 },
        },
        React.createElement(
          'div',
          {
            className: 'flex justify-center items-center h-64',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 843 },
          },
          React.createElement(
            'div',
            {
              className: 'text-center',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 844 },
            },
            React.createElement(Loader, {
              size: 32,
              className: 'animate-spin text-gold-600 mx-auto mb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 845 },
            }),
            React.createElement(
              'p',
              {
                className: 'text-stone-500',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 846 },
              },
              'Loading orders...'
            )
          )
        )
      )
    );
  }

  return React.createElement(
    'div',
    {
      className: 'pt-44 pb-12 min-h-screen bg-stone-50',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 855 },
    },
    toast &&
      React.createElement(
        'div',
        {
          className: `fixed top-6 right-6 z-[999] flex items-center gap-3 px-5 py-4 rounded-sm shadow-xl text-sm font-medium transition-all ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-stone-900 text-white'}`,
          style: { minWidth: '280px' },
        },
        React.createElement('span', null, toast.message),
        React.createElement(
          'button',
          {
            onClick: () => setToast(null),
            className: 'ml-auto text-white/70 hover:text-white text-lg leading-none',
          },
          '×'
        )
      ),
    React.createElement(
      'div',
      {
        className: 'max-w-7xl mx-auto px-6',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 856 },
      },

      /* Header */
      React.createElement(
        'div',
        {
          className: 'flex flex-col md:flex-row justify-between items-center mb-8 gap-4',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 859 },
        },
        React.createElement(
          'div',
          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 860 } },
          (user?.role === 'admin' || (user?.permissions || []).includes('view_dashboard')) &&
            React.createElement(
              'button',
              {
                onClick: () => onNavigate('admin-dashboard'),
                className:
                  'flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 861 },
              },

              React.createElement(ArrowLeft, {
                size: 14,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 865 },
              }),
              ' Back to Dashboard'
            ),
          React.createElement(
            'h1',
            {
              className: 'font-serif text-3xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 867 },
            },
            'Order Management'
          ),
          React.createElement(
            'p',
            {
              className: 'text-stone-500 font-light mt-2 text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 868 },
            },
            'Control order flow, verify payments, and manage shipment tracking.'
          )
        ),

        React.createElement(
          'div',
          {
            className: 'flex items-center gap-3',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 873 },
          },
          React.createElement(
            'button',
            {
              onClick: fetchOrdersFromDatabase,
              className:
                'px-4 py-2 border border-stone-200 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors rounded-sm flex items-center gap-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 874 },
            },

            React.createElement(RefreshCw, {
              size: 14,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 878 },
            }),
            ' Refresh'
          ),
          pendingProofsCount > 0 &&
            React.createElement(
              'div',
              {
                className:
                  'bg-orange-100 text-orange-700 px-3 py-2 rounded-sm text-xs font-bold flex items-center gap-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 881 },
              },
              React.createElement(AlertCircle, {
                size: 14,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 882 },
              }),
              ' ',
              pendingProofsCount,
              ' pending order',
              pendingProofsCount !== 1 ? 's' : '',
              ' waiting for proof'
            )
        )
      ),

      /* API Error Message */
      apiError &&
        React.createElement(
          'div',
          {
            className:
              'mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm flex items-center gap-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 890 },
          },
          React.createElement(AlertCircle, {
            size: 16,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 891 },
          }),
          React.createElement(
            'span',
            {
              className: 'text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 892 },
            },
            apiError
          ),
          React.createElement(
            'button',
            {
              onClick: () => fetchOrdersFromDatabase(),
              className: 'ml-auto text-red-600 hover:text-red-800 underline text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 893 },
            },
            'Retry'
          )
        ),

      /* Error Message */
      error &&
        React.createElement(
          'div',
          {
            className:
              'mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm flex items-center gap-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 901 },
          },
          React.createElement(AlertCircle, {
            size: 16,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 902 },
          }),
          React.createElement(
            'span',
            {
              className: 'text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 903 },
            },
            error
          ),
          React.createElement(
            'button',
            {
              onClick: () => setError(null),
              className: 'ml-auto text-red-500 hover:text-red-700',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 904 },
            },
            React.createElement(X, {
              size: 16,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 905 },
            })
          )
        ),

      /* Orders Table */
      React.createElement(
        'div',
        {
          className: 'bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 911 },
        },
        React.createElement(
          'div',
          {
            className:
              'p-6 border-b border-stone-100 flex flex-col md:flex-row gap-4 justify-between bg-stone-50/30',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 912 },
          },
          React.createElement(
            'div',
            {
              className: 'relative flex-1 max-w-md',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 913 },
            },
            React.createElement(Search, {
              className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
              size: 16,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 914 },
            }),
            React.createElement('input', {
              type: 'text',
              placeholder: 'Search Order ID or Customer...',
              value: searchTerm,
              onChange: (e) => setSearchTerm(e.target.value),
              className:
                'w-full pl-10 pr-4 py-2 border border-stone-200 rounded-sm focus:border-gold-500 focus:outline-none text-sm bg-white',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 915 },
            })
          ),
          React.createElement(
            'div',
            {
              className: 'flex items-center gap-3',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 923 },
            },
            React.createElement(
              'div',
              {
                className: 'flex items-center gap-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 924 },
              },
              React.createElement(Filter, {
                size: 16,
                className: 'text-stone-400',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 925 },
              }),
              React.createElement(
                'select',
                {
                  value: filterStatus,
                  onChange: (e) => setFilterStatus(e.target.value),
                  className:
                    'border border-stone-200 rounded-sm px-3 py-2 text-sm focus:border-gold-500 focus:outline-none bg-white cursor-pointer',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 926 },
                },

                React.createElement(
                  'option',
                  {
                    value: 'All',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 931 },
                  },
                  'All Transactions'
                ),
                React.createElement(
                  'option',
                  {
                    value: 'Pending',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 932 },
                  },
                  'Pending'
                ),
                React.createElement(
                  'option',
                  {
                    value: 'Paid',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 933 },
                  },
                  'Paid'
                ),
                React.createElement(
                  'option',
                  {
                    value: 'Preparing',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 934 },
                  },
                  'Preparing'
                ),
                React.createElement(
                  'option',
                  {
                    value: 'Shipped',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 935 },
                  },
                  'Shipped'
                ),
                React.createElement(
                  'option',
                  {
                    value: 'Ready for Pickup',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 936 },
                  },
                  'Ready for Pickup'
                ),
                React.createElement(
                  'option',
                  {
                    value: 'Picked Up',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 937 },
                  },
                  'Picked Up'
                ),
                React.createElement(
                  'option',
                  {
                    value: 'Delivered',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 938 },
                  },
                  'Delivered'
                ),
                React.createElement(
                  'option',
                  {
                    value: 'Rejected',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 939 },
                  },
                  'Rejected'
                )
              )
            )
          )
        ),

        React.createElement(
          'div',
          {
            className: 'overflow-x-auto',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 945 },
          },
          React.createElement(
            'table',
            {
              className: 'w-full text-left border-collapse',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 946 },
            },
            React.createElement(
              'thead',
              {
                className:
                  'bg-stone-100 text-stone-500 text-[10px] uppercase tracking-widest font-bold',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 947 },
              },
              React.createElement(
                'tr',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 948 } },
                React.createElement(
                  'th',
                  {
                    className: 'px-6 py-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 949 },
                  },
                  'Order Details'
                ),
                React.createElement(
                  'th',
                  {
                    className: 'px-6 py-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 950 },
                  },
                  'Delivery'
                ),
                React.createElement(
                  'th',
                  {
                    className: 'px-6 py-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 951 },
                  },
                  'Payment Method'
                ),
                React.createElement(
                  'th',
                  {
                    className: 'px-6 py-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 952 },
                  },
                  'Amount'
                ),
                React.createElement(
                  'th',
                  {
                    className: 'px-6 py-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 953 },
                  },
                  'Status'
                ),
                React.createElement(
                  'th',
                  {
                    className: 'px-6 py-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 954 },
                  },
                  'Proof'
                ),
                React.createElement(
                  'th',
                  {
                    className: 'px-6 py-4 text-right',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 955 },
                  },
                  'Actions'
                )
              )
            ),
            React.createElement(
              'tbody',
              {
                className: 'divide-y divide-stone-100',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 958 },
              },
              filteredOrders.length > 0
                ? filteredOrders.map((order) => {
                    const customerName = getCustomerName(order);
                    const paymentInfo = getPaymentMethodDisplay(order);
                    const deliveryInfo = getDeliveryTypeDisplay(order);
                    const isRejected = order.status === 'Rejected';
                    const hasProofValue = hasProof(order);
                    const isPickup = isPickupOrder(order);

                    // Normalize status display
                    const displayStatus = order.status === 'Preparing' ? 'Preparing' : order.status;

                    return React.createElement(
                      'tr',
                      {
                        key: order.id,
                        className: `hover:bg-stone-50/50 transition-colors ${isRejected ? 'bg-red-50/30' : ''}`,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 972 },
                      },
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 973 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: 'flex flex-col',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 974 },
                          },
                          React.createElement(
                            'span',
                            {
                              className: 'font-bold text-sm',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 975 },
                            },
                            customerName
                          ),
                          React.createElement(
                            'span',
                            {
                              className: 'text-xs text-stone-500',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 976 },
                            },
                            'Order #',
                            order.order_number || order.id
                          ),
                          React.createElement(
                            'span',
                            {
                              className: 'text-[10px] text-stone-400',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 977 },
                            },
                            order.date || order.orderDate
                          ),
                          isPickup &&
                            order.pickup_name &&
                            React.createElement(
                              'span',
                              {
                                className: 'text-[9px] text-amber-600 mt-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 979 },
                              },
                              'Pickup: ',
                              order.pickup_name
                            ),
                          order.rejection_reason &&
                            React.createElement(
                              'span',
                              {
                                className:
                                  'text-[9px] text-red-600 bg-red-50 px-1 py-0.5 rounded inline-flex items-center gap-1 w-fit mt-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 982 },
                              },
                              React.createElement(MessageSquare, {
                                size: 8,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 983 },
                              }),
                              ' Rejected: ',
                              order.rejection_reason.substring(0, 30),
                              '...'
                            )
                        )
                      ),
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 988 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: 'flex items-center gap-2',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 989 },
                          },
                          deliveryInfo.icon,
                          React.createElement(
                            'span',
                            {
                              className: 'text-xs',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 991 },
                            },
                            deliveryInfo.text
                          )
                        ),
                        isPickup &&
                          React.createElement(
                            'div',
                            {
                              className: 'text-[9px] text-amber-600 mt-1',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 994 },
                            },
                            order.pickup_name && `Pickup by: ${order.pickup_name}`
                          )
                      ),
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 999 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: 'flex flex-col gap-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1000 },
                          },
                          React.createElement(
                            'div',
                            {
                              className: 'flex items-center gap-2 text-xs font-medium',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1001 },
                            },
                            paymentInfo.icon,
                            paymentInfo.text
                          ),
                          order.transactionId &&
                            React.createElement(
                              'span',
                              {
                                className: 'text-[10px] font-mono text-stone-400',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1006 },
                              },
                              'Ref: ',
                              order.transactionId
                            )
                        )
                      ),
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4 font-serif',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1010 },
                        },
                        '₱ ',
                        _optionalChain([
                          order,
                          'access',
                          (_15) => _15.total,
                          'optionalAccess',
                          (_16) => _16.toLocaleString,
                          'call',
                          (_17) => _17(),
                        ]) || '0'
                      ),
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1013 },
                        },
                        React.createElement(
                          'span',
                          {
                            className: `inline-block px-2 py-1 rounded-full text-[10px] uppercase font-bold tracking-wide ${
                              displayStatus === 'Paid'
                                ? 'bg-green-100 text-green-700'
                                : displayStatus === 'Pending'
                                  ? 'bg-orange-100 text-orange-700'
                                  : displayStatus === 'Preparing'
                                    ? 'bg-purple-100 text-purple-700'
                                    : displayStatus === 'Shipped'
                                      ? 'bg-blue-100 text-blue-700'
                                      : displayStatus === 'Ready for Pickup'
                                        ? 'bg-amber-100 text-amber-700'
                                        : displayStatus === 'Picked Up'
                                          ? 'bg-emerald-100 text-emerald-700'
                                          : displayStatus === 'Delivered'
                                            ? 'bg-emerald-100 text-emerald-700'
                                            : displayStatus === 'Rejected'
                                              ? 'bg-red-100 text-red-700'
                                              : 'bg-stone-100 text-stone-500'
                            }`,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1014 },
                          },
                          displayStatus
                        )
                      ),
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1028 },
                        },
                        hasProofValue
                          ? React.createElement(
                              'button',
                              {
                                onClick: () => {
                                  setSelectedOrder(order);
                                  setViewMode('payment');
                                  fetchPaymentProof(order.id);
                                },
                                className:
                                  'text-xs text-green-600 hover:text-green-700 flex items-center gap-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1030 },
                              },

                              React.createElement(Eye, {
                                size: 12,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1038 },
                              }),
                              ' View Proof'
                            )
                          : React.createElement(
                              'span',
                              {
                                className: 'text-xs text-stone-400',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1041 },
                              },
                              'No proof'
                            )
                      ),
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4 text-right',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1044 },
                        },
                        React.createElement(
                          'button',
                          {
                            onClick: () => openManageModal(order),
                            className:
                              'px-3 py-1.5 text-xs uppercase tracking-wider transition-colors rounded-sm flex items-center gap-2 ml-auto shadow-sm bg-stone-900 text-white hover:bg-gold-600',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1045 },
                          },

                          React.createElement(Info, {
                            size: 12,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1049 },
                          }),
                          ' View'
                        )
                      )
                    );
                  })
                : React.createElement(
                    'tr',
                    { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1056 } },
                    React.createElement(
                      'td',
                      {
                        colSpan: 7,
                        className: 'px-6 py-12 text-center text-stone-500',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1057 },
                      },
                      apiError ? 'Unable to load orders. Please refresh.' : 'No orders found'
                    )
                  )
            )
          )
        )
      ),

      /* Lightbox Modal */
      lightboxOpen &&
        React.createElement(
          'div',
          {
            className: 'fixed inset-0 z-[200] bg-black/95 flex items-center justify-center',
            onClick: closeLightbox,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1069 },
          },
          React.createElement(
            'button',
            {
              onClick: closeLightbox,
              className:
                'absolute top-5 right-5 text-white hover:text-gold-600 z-10 bg-black/50 p-2 rounded-full',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1070 },
            },
            React.createElement(XCircle, {
              size: 30,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1071 },
            })
          ),
          React.createElement('img', {
            src: lightboxImage,
            alt: 'Full size view',
            className: 'max-w-[90vw] max-h-[90vh] object-contain',
            onClick: (e) => e.stopPropagation(),
            onError: (e) => {
              e.target.src = PLACEHOLDER_IMAGE;
            },
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1073 },
          })
        ),

      /* Reject Modal */
      showRejectModal &&
        React.createElement(
          'div',
          {
            className: 'fixed inset-0 z-[150] flex items-center justify-center',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1087 },
          },
          React.createElement('div', {
            className: 'absolute inset-0 bg-stone-900/60 backdrop-blur-sm',
            onClick: () => setShowRejectModal(false),
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1088 },
          }),
          React.createElement(
            'div',
            {
              className:
                'relative bg-white w-full max-w-md rounded-sm shadow-2xl p-6 animate-fade-up-enter mx-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1089 },
            },
            React.createElement(
              'div',
              {
                className: 'flex justify-between items-center mb-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1090 },
              },
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-xl',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1091 },
                },
                'Reject Payment'
              ),
              React.createElement(
                'button',
                {
                  onClick: () => setShowRejectModal(false),
                  className: 'text-stone-400 hover:text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1092 },
                },
                React.createElement(X, {
                  size: 20,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1093 },
                })
              )
            ),
            React.createElement(
              'p',
              {
                className: 'text-xs text-stone-500 mb-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1096 },
              },
              'Please provide a reason for rejecting this payment.'
            ),
            React.createElement('textarea', {
              value: rejectionReason,
              onChange: (e) => setRejectionReason(e.target.value),
              rows: 4,
              className:
                'w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 outline-none mb-4 resize-none',
              placeholder: 'e.g., Proof of payment is unclear, Incorrect amount...',
              autoFocus: true,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1097 },
            }),
            React.createElement(
              'div',
              {
                className: 'flex gap-3 justify-end',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1105 },
              },
              React.createElement(
                'button',
                {
                  onClick: () => setShowRejectModal(false),
                  className: 'text-xs text-stone-500 hover:text-stone-900 px-4 py-2',
                  disabled: isRejecting,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1106 },
                },
                'Cancel'
              ),
              React.createElement(
                'button',
                {
                  onClick: handleRejectWithReason,
                  disabled: !rejectionReason.trim() || isRejecting,
                  className:
                    'bg-red-600 text-white px-6 py-2 text-xs uppercase tracking-widest hover:bg-red-700 disabled:opacity-50 flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1113 },
                },

                isRejecting
                  ? React.createElement(
                      React.Fragment,
                      null,
                      React.createElement(Loader, {
                        size: 14,
                        className: 'animate-spin',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1118 },
                      }),
                      ' Rejecting...'
                    )
                  : 'Confirm Rejection'
              )
            )
          )
        ),

      /* Order Details Modal */
      selectedOrder &&
        React.createElement(
          'div',
          {
            className: 'fixed inset-0 z-[100] flex items-center justify-center',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1127 },
          },
          React.createElement('div', {
            className: 'absolute inset-0 bg-stone-900/60 backdrop-blur-sm',
            onClick: () => setSelectedOrder(null),
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1128 },
          }),

          React.createElement(
            'div',
            {
              className:
                'relative bg-white w-full max-w-3xl h-[90vh] overflow-y-auto rounded-sm shadow-2xl animate-fade-up-enter mx-4 flex flex-col',
              onClick: handleModalContentClick,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1130 },
            },

            /* Modal Header */
            React.createElement(
              'div',
              {
                className:
                  'p-6 border-b border-stone-100 flex justify-between items-center bg-stone-50 sticky top-0 z-10',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1135 },
              },
              React.createElement(
                'div',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1136 } },
                React.createElement(
                  'h3',
                  {
                    className: 'font-serif text-2xl',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1137 },
                  },
                  'Order #',
                  selectedOrder.order_number || selectedOrder.id
                ),
                React.createElement(
                  'p',
                  {
                    className: 'text-xs text-stone-500',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1138 },
                  },
                  getCustomerName(selectedOrder),
                  ' • ',
                  selectedOrder.date || selectedOrder.orderDate
                ),
                isPickupOrder(selectedOrder) &&
                  React.createElement(
                    'div',
                    {
                      className:
                        'mt-2 inline-flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded-sm',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1140 },
                    },
                    React.createElement(Store, {
                      size: 12,
                      className: 'text-amber-600',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1141 },
                    }),
                    React.createElement(
                      'span',
                      {
                        className: 'text-[10px] text-amber-700 font-medium',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1142 },
                      },
                      'Store Pickup'
                    )
                  )
              ),
              React.createElement(
                'button',
                {
                  onClick: () => setSelectedOrder(null),
                  className: 'text-stone-400 hover:text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1146 },
                },
                React.createElement(XCircle, {
                  size: 24,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1147 },
                })
              )
            ),

            /* Tabs */
            React.createElement(
              'div',
              {
                className: 'flex border-b border-stone-200',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1152 },
              },
              React.createElement(
                'button',
                {
                  onClick: () => handleTabChange('info'),
                  className: `flex-1 py-4 text-xs uppercase tracking-widest font-bold ${viewMode === 'info' ? 'border-b-2 border-gold-600 text-gold-600 bg-white' : 'text-stone-500 bg-stone-50 hover:bg-stone-100'}`,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1153 },
                },
                'Customer Info'
              ),
              React.createElement(
                'button',
                {
                  onClick: () => handleTabChange('payment'),
                  className: `flex-1 py-4 text-xs uppercase tracking-widest font-bold ${viewMode === 'payment' ? 'border-b-2 border-gold-600 text-gold-600 bg-white' : 'text-stone-500 bg-stone-50 hover:bg-stone-100'}`,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1159 },
                },
                'Payment ',
                proofLoading &&
                  React.createElement(Loader, {
                    size: 12,
                    className: 'inline ml-1 animate-spin',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1163 },
                  })
              ),
              React.createElement(
                'button',
                {
                  onClick: () => handleTabChange('fulfillment'),
                  className: `flex-1 py-4 text-xs uppercase tracking-widest font-bold ${viewMode === 'fulfillment' ? 'border-b-2 border-gold-600 text-gold-600 bg-white' : 'text-stone-500 bg-stone-50 hover:bg-stone-100'}`,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1165 },
                },
                'Fulfillment'
              )
            ),

            /* Tab Content */
            React.createElement(
              'div',
              {
                className: 'p-8 flex-1 overflow-y-auto',
                onClick: handleModalContentClick,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1174 },
              },
              /* Customer Info Tab */
              viewMode === 'info' &&
                React.createElement(
                  'div',
                  {
                    className: 'space-y-6',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1177 },
                  },
                  React.createElement(
                    'div',
                    {
                      className: 'bg-stone-50 p-6 rounded-sm border border-stone-200',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1178 },
                    },
                    React.createElement(
                      'h4',
                      {
                        className:
                          'text-xs uppercase tracking-widest text-stone-500 font-bold mb-4 flex items-center gap-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1179 },
                      },
                      React.createElement(User, {
                        size: 14,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1180 },
                      }),
                      ' Customer Information'
                    ),
                    React.createElement(
                      'div',
                      {
                        className: 'grid grid-cols-2 gap-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1182 },
                      },
                      React.createElement(
                        'div',
                        { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1183 } },
                        React.createElement(
                          'p',
                          {
                            className: 'text-[10px] text-stone-400',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1184 },
                          },
                          'Name'
                        ),
                        React.createElement(
                          'p',
                          {
                            className: 'text-sm font-medium',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1185 },
                          },
                          getCustomerName(selectedOrder)
                        )
                      ),
                      React.createElement(
                        'div',
                        { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1187 } },
                        React.createElement(
                          'p',
                          {
                            className: 'text-[10px] text-stone-400',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1188 },
                          },
                          'Email'
                        ),
                        React.createElement(
                          'p',
                          {
                            className: 'text-sm',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1189 },
                          },
                          selectedOrder.customer_email || selectedOrder.pickup_email || 'N/A'
                        )
                      ),
                      React.createElement(
                        'div',
                        { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1191 } },
                        React.createElement(
                          'p',
                          {
                            className: 'text-[10px] text-stone-400',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1192 },
                          },
                          'Phone'
                        ),
                        React.createElement(
                          'p',
                          {
                            className: 'text-sm',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1193 },
                          },
                          selectedOrder.customer_phone || selectedOrder.pickup_phone || 'N/A'
                        )
                      ),
                      React.createElement(
                        'div',
                        {
                          className: 'col-span-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1195 },
                        },
                        React.createElement(
                          'p',
                          {
                            className: 'text-[10px] text-stone-400',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1196 },
                          },
                          isPickupOrder(selectedOrder) ? 'Pickup Location' : 'Shipping Address'
                        ),
                        React.createElement(
                          'p',
                          {
                            className:
                              'text-sm bg-white p-3 rounded-sm border border-stone-200 mt-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1199 },
                          },
                          isPickupOrder(selectedOrder)
                            ? 'Sparkling Gems Jewelry Shop - 2F, San Antonio Building, Corumi, Quezon City'
                            : selectedOrder.shippingAddress || 'No shipping address provided'
                        )
                      ),
                      isPickupOrder(selectedOrder) &&
                        React.createElement(
                          'div',
                          {
                            className:
                              'col-span-2 bg-amber-50 p-3 rounded-sm border border-amber-200',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1206 },
                          },
                          React.createElement(
                            'p',
                            {
                              className: 'text-xs text-amber-800',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1207 },
                            },
                            React.createElement(
                              'span',
                              {
                                className: 'font-bold',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1208 },
                              },
                              'Pickup Person:'
                            ),
                            ' ',
                            selectedOrder.pickup_name,
                            React.createElement('br', {
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1208 },
                            }),
                            React.createElement(
                              'span',
                              {
                                className: 'font-bold',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1209 },
                              },
                              'Contact:'
                            ),
                            ' ',
                            selectedOrder.pickup_phone
                          )
                        )
                    )
                  ),

                  React.createElement(
                    'div',
                    {
                      className: 'bg-stone-50 p-6 rounded-sm border border-stone-200',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1216 },
                    },
                    React.createElement(
                      'h4',
                      {
                        className:
                          'text-xs uppercase tracking-widest text-stone-500 font-bold mb-4 flex items-center gap-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1217 },
                      },
                      React.createElement(Package, {
                        size: 14,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1218 },
                      }),
                      ' Order Items'
                    ),
                    React.createElement(
                      'div',
                      {
                        className: 'space-y-3',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1220 },
                      },
                      _optionalChain([
                        selectedOrder,
                        'access',
                        (_18) => _18.items,
                        'optionalAccess',
                        (_19) => _19.map,
                        'call',
                        (_20) =>
                          _20((item, idx) =>
                            React.createElement(
                              'div',
                              {
                                key: idx,
                                className: 'flex items-center gap-3 p-2 bg-white rounded-sm',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1222 },
                              },
                              React.createElement(
                                'div',
                                {
                                  className: 'w-12 h-12 bg-stone-100 rounded-sm overflow-hidden',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1223 },
                                },
                                item.image
                                  ? React.createElement('img', {
                                      src: item.image,
                                      alt: item.name,
                                      className: 'w-full h-full object-cover',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1225 },
                                    })
                                  : React.createElement(ImageIcon, {
                                      size: 16,
                                      className: 'text-stone-300 m-auto',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1227 },
                                    })
                              ),
                              React.createElement(
                                'div',
                                {
                                  className: 'flex-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1230 },
                                },
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-sm font-medium',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1231 },
                                  },
                                  item.name
                                ),
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-xs text-stone-500',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1232 },
                                  },
                                  'Qty: ',
                                  item.quantity,
                                  ' • ₱',
                                  _optionalChain([
                                    item,
                                    'access',
                                    (_21) => _21.price,
                                    'optionalAccess',
                                    (_22) => _22.toLocaleString,
                                    'call',
                                    (_23) => _23(),
                                  ])
                                )
                              )
                            )
                          ),
                      ])
                    )
                  )
                ),

              /* Payment Tab */
              viewMode === 'payment' &&
                React.createElement(
                  'div',
                  {
                    className: 'space-y-6',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1243 },
                  },
                  React.createElement(
                    'div',
                    {
                      className: `p-4 rounded-sm border ${
                        selectedOrder.status === 'Paid'
                          ? 'bg-green-50 border-green-200'
                          : selectedOrder.status === 'Rejected'
                            ? 'bg-red-50 border-red-200'
                            : 'bg-orange-50 border-orange-200'
                      }`,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1244 },
                    },
                    React.createElement(
                      'div',
                      {
                        className: 'flex items-center gap-2 mb-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1248 },
                      },
                      React.createElement(
                        'span',
                        {
                          className: 'text-gold-600 font-bold text-lg',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1249 },
                        },
                        '₱'
                      ),
                      React.createElement(
                        'span',
                        {
                          className: `text-sm font-bold ${
                            selectedOrder.status === 'Paid'
                              ? 'text-green-800'
                              : selectedOrder.status === 'Rejected'
                                ? 'text-red-800'
                                : 'text-orange-800'
                          }`,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1250 },
                        },
                        'Status: ',
                        selectedOrder.status
                      )
                    ),
                    React.createElement(
                      'p',
                      {
                        className: 'text-xs',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1257 },
                      },
                      'Total: ₱ ',
                      _optionalChain([
                        selectedOrder,
                        'access',
                        (_24) => _24.total,
                        'optionalAccess',
                        (_25) => _25.toLocaleString,
                        'call',
                        (_26) => _26(),
                      ]) || '0'
                    ),
                    React.createElement(
                      'p',
                      {
                        className: 'text-xs mt-1',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1258 },
                      },
                      'Method: ',
                      getPaymentMethodDisplay(selectedOrder).text
                    ),
                    selectedOrder.transactionId &&
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs mt-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1259 },
                        },
                        'Ref: ',
                        selectedOrder.transactionId
                      ),
                    selectedOrder.proof_uploaded_at &&
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs text-stone-500 mt-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1261 },
                        },
                        'Uploaded: ',
                        new Date(selectedOrder.proof_uploaded_at).toLocaleString()
                      ),
                    _optionalChain([
                      paymentProofData,
                      'optionalAccess',
                      (_27) => _27.proof_source,
                    ]) &&
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs text-stone-400 mt-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1264 },
                        },
                        'Source: ',
                        paymentProofData.proof_source
                      )
                  ),

                  React.createElement(
                    'div',
                    { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1268 } },
                    React.createElement(
                      'h4',
                      {
                        className:
                          'text-xs uppercase tracking-widest text-stone-500 font-bold mb-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1269 },
                      },
                      'Proof of Payment'
                    ),
                    proofLoading
                      ? React.createElement(
                          'div',
                          {
                            className: 'flex justify-center py-12',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1271 },
                          },
                          React.createElement(Loader, {
                            size: 32,
                            className: 'animate-spin text-gold-600',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1272 },
                          }),
                          React.createElement(
                            'p',
                            {
                              className: 'text-sm text-stone-500 ml-2',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1273 },
                            },
                            'Loading proof...'
                          )
                        )
                      : (() => {
                          const proofImage = getPaymentProofImage();
                          if (proofImage) {
                            return React.createElement(
                              'div',
                              {
                                className: 'border border-stone-200 p-4 rounded-sm bg-stone-50',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1279 },
                              },
                              React.createElement('img', {
                                src: proofImage,
                                alt: 'Payment Proof',
                                className: 'max-h-64 object-contain cursor-pointer mx-auto',
                                onClick: () => openLightbox(proofImage),
                                onError: (e) => {
                                  e.target.src = PLACEHOLDER_IMAGE;
                                },
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1280 },
                              }),
                              React.createElement(
                                'p',
                                {
                                  className: 'text-xs text-center text-stone-500 mt-2',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1290 },
                                },
                                selectedOrder.proof_file_name &&
                                  `File: ${selectedOrder.proof_file_name}`
                              ),
                              _optionalChain([
                                paymentProofData,
                                'optionalAccess',
                                (_28) => _28.status,
                              ]) &&
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-xs text-center text-stone-400 mt-1',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1294 },
                                  },
                                  'Status: ',
                                  paymentProofData.status
                                )
                            );
                          }
                          return React.createElement(
                            'div',
                            {
                              className:
                                'h-40 border-2 border-dashed border-stone-300 rounded-sm flex items-center justify-center text-stone-400',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1302 },
                            },
                            React.createElement(FileText, {
                              size: 32,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1303 },
                            }),
                            React.createElement(
                              'p',
                              {
                                className: 'text-sm ml-2',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1304 },
                              },
                              'No proof uploaded'
                            )
                          );
                        })()
                  ),

                  selectedOrder.status === 'Pending' &&
                    getPaymentProofImage() &&
                    React.createElement(
                      'div',
                      {
                        className: 'flex gap-4 pt-6',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1311 },
                      },
                      React.createElement(
                        'button',
                        {
                          onClick: handleApprovePayment,
                          className:
                            'flex-1 bg-stone-900 text-white py-3 uppercase text-xs hover:bg-gold-600 transition-colors rounded-sm',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1312 },
                        },
                        'Confirm Payment'
                      ),
                      React.createElement(
                        'button',
                        {
                          onClick: () => setShowRejectModal(true),
                          className:
                            'flex-1 border border-red-200 text-red-500 py-3 uppercase text-xs hover:bg-red-50 transition-colors rounded-sm',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1318 },
                        },
                        'Reject'
                      )
                    )
                ),

              /* Fulfillment Tab */
              viewMode === 'fulfillment' &&
                React.createElement(
                  'div',
                  {
                    className: 'space-y-8',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1331 },
                  },
                  React.createElement(
                    'div',
                    {
                      className: 'flex justify-end',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1332 },
                    },
                    React.createElement(
                      'button',
                      {
                        onClick: handleSaveFulfillment,
                        disabled: isSavingFulfillment || !hasPendingChanges(),
                        className: `px-6 py-3 text-white text-xs uppercase tracking-widest rounded-sm flex items-center gap-2 shadow-lg ${
                          hasPendingChanges()
                            ? 'bg-gold-600 hover:bg-gold-700'
                            : 'bg-stone-400 cursor-not-allowed'
                        }`,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1333 },
                      },

                      isSavingFulfillment
                        ? React.createElement(
                            React.Fragment,
                            null,
                            React.createElement(Loader, {
                              size: 16,
                              className: 'animate-spin',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1340 },
                            }),
                            ' Saving...'
                          )
                        : React.createElement(
                            React.Fragment,
                            null,
                            React.createElement(Save, {
                              size: 16,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1340 },
                            }),
                            ' Save Changes'
                          )
                    )
                  ),

                  React.createElement(
                    'div',
                    {
                      className: 'grid grid-cols-2 gap-6',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1344 },
                    },
                    React.createElement(
                      'div',
                      { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1345 } },
                      React.createElement(
                        'label',
                        {
                          className: 'block text-xs uppercase tracking-widest font-bold mb-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1346 },
                        },
                        'Order Status'
                      ),
                      React.createElement(
                        'select',
                        {
                          value: _nullishCoalesce(pendingStatus, () => selectedOrder.status),
                          onChange: handleStatusChange,
                          className:
                            'w-full border border-stone-300 p-2 rounded-sm text-sm bg-white',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1347 },
                        },

                        React.createElement(
                          'option',
                          {
                            value: 'Pending',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1352 },
                          },
                          'Pending'
                        ),
                        React.createElement(
                          'option',
                          {
                            value: 'Paid',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1353 },
                          },
                          'Paid'
                        ),
                        React.createElement(
                          'option',
                          {
                            value: 'Preparing',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1354 },
                          },
                          'Preparing'
                        ),
                        React.createElement(
                          'option',
                          {
                            value: 'Shipped',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1355 },
                          },
                          'Shipped'
                        ),
                        React.createElement(
                          'option',
                          {
                            value: 'Ready for Pickup',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1356 },
                          },
                          'Ready for Pickup'
                        ),
                        React.createElement(
                          'option',
                          {
                            value: 'Picked Up',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1357 },
                          },
                          'Picked Up'
                        ),
                        React.createElement(
                          'option',
                          {
                            value: 'Delivered',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1358 },
                          },
                          'Delivered'
                        ),
                        React.createElement(
                          'option',
                          {
                            value: 'Rejected',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1359 },
                          },
                          'Rejected'
                        )
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-[10px] text-stone-400 mt-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1361 },
                        },
                        pendingStatus &&
                          `Auto-log: ${getAutoLogDescription(pendingStatus).substring(0, 60)}...`
                      )
                    ),
                    React.createElement(
                      'div',
                      { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1365 } },
                      React.createElement(
                        'label',
                        {
                          className: 'block text-xs uppercase tracking-widest font-bold mb-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1366 },
                        },
                        'Tracking Number'
                      ),
                      React.createElement('input', {
                        type: 'text',
                        value: pendingTrackingNumber,
                        onChange: handleTrackingChange,
                        placeholder: isPickupOrder(selectedOrder)
                          ? 'N/A for pickup'
                          : 'LBC-12345678',
                        disabled: isPickupOrder(selectedOrder),
                        className: `w-full border border-stone-300 p-2 rounded-sm text-sm bg-white ${isPickupOrder(selectedOrder) ? 'opacity-50' : ''}`,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1367 },
                      })
                    )
                  ),

                  React.createElement(
                    'div',
                    {
                      className: 'border-t border-stone-100 pt-6',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1378 },
                    },
                    React.createElement(
                      'h4',
                      {
                        className: 'text-xs uppercase tracking-widest font-bold mb-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1379 },
                      },
                      'Add Timeline Entry'
                    ),
                    React.createElement(
                      'div',
                      {
                        className: 'bg-stone-50 p-4 rounded-sm border border-stone-200',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1380 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'grid grid-cols-3 gap-4 mb-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1381 },
                        },
                        React.createElement('input', {
                          type: 'text',
                          placeholder: 'Log Details (auto-filled when status changes)',
                          value: pendingLogNote,
                          onChange: handleLogNoteChange,
                          className:
                            'col-span-2 border border-stone-300 p-2 text-sm rounded-sm bg-white',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1382 },
                        }),
                        React.createElement('input', {
                          type: 'text',
                          placeholder: 'Location',
                          value: pendingLogLocation,
                          onChange: handleLogLocationChange,
                          className: 'border border-stone-300 p-2 text-sm rounded-sm bg-white',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1389 },
                        })
                      ),
                      React.createElement(
                        'div',
                        {
                          className: 'flex items-center gap-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1397 },
                        },
                        React.createElement(
                          'label',
                          {
                            className:
                              'cursor-pointer bg-stone-100 hover:bg-stone-200 px-4 py-2 rounded-sm text-xs transition-colors flex items-center gap-2',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1398 },
                          },
                          React.createElement(Upload, {
                            size: 12,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1399 },
                          }),
                          'Upload Image',

                          React.createElement('input', {
                            type: 'file',
                            className: 'hidden',
                            accept: 'image/*',
                            onChange: handleTrackingFileUpload,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1401 },
                          })
                        ),
                        pendingLogProof &&
                          React.createElement(
                            'div',
                            {
                              className:
                                'relative w-16 h-16 border border-stone-200 rounded-sm overflow-hidden',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1409 },
                            },
                            React.createElement('img', {
                              src: pendingLogProof,
                              alt: 'Preview',
                              className: 'w-full h-full object-cover',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1410 },
                            }),
                            React.createElement(
                              'button',
                              {
                                onClick: () => {
                                  setPendingLogProof(null);
                                  setPendingLogProofFile(null);
                                },
                                className:
                                  'absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1411 },
                              },

                              React.createElement(X, {
                                size: 12,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1418 },
                              })
                            )
                          )
                      )
                    )
                  ),

                  React.createElement(
                    'div',
                    { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1426 } },
                    React.createElement(
                      'h4',
                      {
                        className: 'text-xs uppercase tracking-widest font-bold mb-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1427 },
                      },
                      'Timeline History'
                    ),
                    React.createElement(
                      'div',
                      {
                        className: 'space-y-4 max-h-60 overflow-y-auto',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1428 },
                      },
                      orderTimeline.length > 0
                        ? orderTimeline.map((event, idx) =>
                            React.createElement(
                              'div',
                              {
                                key: idx,
                                className:
                                  'flex gap-4 text-sm border-l-2 border-gold-400 pl-4 relative',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1430 },
                              },
                              React.createElement('div', {
                                className:
                                  'absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-gold-600 border-2 border-white',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1431 },
                              }),
                              React.createElement(
                                'div',
                                {
                                  className: 'text-xs text-stone-400 w-24',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1432 },
                                },
                                new Date(event.created_at).toLocaleDateString(),
                                React.createElement('br', {
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1433 },
                                }),
                                React.createElement(
                                  'span',
                                  {
                                    className: 'text-[9px]',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1434 },
                                  },
                                  new Date(event.created_at).toLocaleTimeString()
                                )
                              ),
                              React.createElement(
                                'div',
                                {
                                  className: 'flex-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1436 },
                                },
                                React.createElement(
                                  'p',
                                  {
                                    className: 'font-bold',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1437 },
                                  },
                                  event.status === 'Processing'
                                    ? 'Preparing'
                                    : event.status || 'Update'
                                ),
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-stone-600',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1440 },
                                  },
                                  event.description || event.notes
                                ),
                                event.location &&
                                  event.location !== 'Admin Portal' &&
                                  event.location !== 'admin' &&
                                  React.createElement(
                                    'p',
                                    {
                                      className: 'text-xs text-stone-400',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1442 },
                                    },
                                    'Location: ',
                                    event.location
                                  ),
                                event.proof_image &&
                                  React.createElement(
                                    'div',
                                    {
                                      className:
                                        'mt-2 w-20 h-20 border border-stone-200 rounded-sm overflow-hidden cursor-pointer',
                                      onClick: () => openLightbox(getTimelineImageUrl(event)),
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1445 },
                                    },
                                    React.createElement('img', {
                                      src: getTimelineImageUrl(event),
                                      alt: 'Proof',
                                      className: 'w-full h-full object-cover',
                                      onError: (e) => {
                                        e.target.src = PLACEHOLDER_IMAGE;
                                      },
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1447 },
                                    })
                                  )
                              )
                            )
                          )
                        : React.createElement(
                            'p',
                            {
                              className: 'text-sm text-stone-400 italic text-center',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1459 },
                            },
                            'No timeline entries yet. Add a timeline entry to track this order.'
                          )
                    )
                  )
                )
            )
          )
        )
    )
  );
};

export default AdminTransactions;
